// Congés : demandes à traiter, planning de l'année, soldes.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Snackbar, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip,
} from '@mui/material';
import { AddOutlined, CheckOutlined, CloseOutlined, EditOutlined, BlockOutlined } from '@mui/icons-material';
import Layout from '../../components/Layout';
import CongeDialog from '../../components/rh/CongeDialog';
import { rhAPI } from '../../services/api';
import { apiError, dateFr } from '../../utils/format';
import { STATUTS_CONGE, TYPES_CONGE } from '../../utils/rh';
import { tokens } from '../../theme';

const RhConges = () => {
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [tab, setTab] = useState(0);
  const [conges, setConges] = useState([]);
  const [enAttente, setEnAttente] = useState([]);
  const [soldes, setSoldes] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [decision, setDecision] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => { rhAPI.employes().then((r) => setEmployes(r.data)).catch(() => {}); }, []);
  const load = useCallback(() => {
    Promise.all([rhAPI.conges({ annee }), rhAPI.soldes(annee), rhAPI.conges({ annee: 'tous', statut: 'DEMANDE' })])
      .then(([c, s, p]) => { setConges(c.data); setSoldes(s.data); setEnAttente(p.data); })
      .catch((e) => setError(apiError(e)));
  }, [annee]);
  useEffect(() => { load(); }, [load]);

  const list = tab === 0 ? enAttente : conges;

  const decide = async () => {
    try {
      const r = await rhAPI.decideConge(decision.c.id, { statut: decision.statut, commentaire: decision.commentaire });
      setToast(r.data.message); setDecision(null); load();
    } catch (e) { setDecision({ ...decision, error: apiError(e) }); }
  };

  const years = [annee - 1, annee, annee + 1].filter((v, i, a) => a.indexOf(v) === i);

  return (
    <Layout title="Congés" plain
      actions={<Button variant="contained" startIcon={<AddOutlined />} onClick={() => setDialog({})}>Nouveau congé</Button>}>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ flex: 1 }}>
          <Tab label={<Badge color="warning" badgeContent={enAttente.length} sx={{ pr: 1.5 }}>À traiter</Badge>} />
          <Tab label="Tous les congés" />
          <Tab label="Soldes" />
        </Tabs>
        <TextField select size="small" label="Année" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} sx={{ width: 110 }}>
          {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
      </Box>

      {tab < 2 && (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employé</TableCell><TableCell>Type</TableCell><TableCell>Du</TableCell><TableCell>Au</TableCell>
                  <TableCell align="right">Jours</TableCell><TableCell>Motif</TableCell><TableCell>Statut</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((c) => {
                  const s = soldes.find((x) => x.id === c.employe_id);
                  const depasse = c.statut === 'DEMANDE' && c.type === 'ANNUEL' && s && c.nb_jours > s.solde;
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell><b>{c.nom} {c.prenom}</b><br /><span style={{ fontSize: 12, color: '#5B6675' }}>{c.matricule}</span></TableCell>
                      <TableCell>{TYPES_CONGE[c.type]}</TableCell>
                      <TableCell>{dateFr(c.date_debut)}</TableCell>
                      <TableCell>{dateFr(c.date_fin)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono }}>
                        {c.nb_jours}
                        {depasse && <Tooltip title={`Dépasse le solde (${s.solde} j)`}><Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>!</Box></Tooltip>}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        {c.motif}
                        {c.decision_commentaire && <Box sx={{ fontSize: 12, color: 'text.secondary' }}>Décision : {c.decision_commentaire}</Box>}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={STATUTS_CONGE[c.statut].label} color={STATUTS_CONGE[c.statut].color} />
                        {c.decide_par && <Box sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>par {c.decide_par}</Box>}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {c.statut === 'DEMANDE' && (
                          <>
                            <Tooltip title="Approuver"><IconButton size="small" color="success" aria-label={`Approuver ${c.nom}`} onClick={() => setDecision({ c, statut: 'APPROUVE', commentaire: '' })}><CheckOutlined fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Refuser"><IconButton size="small" color="error" aria-label={`Refuser ${c.nom}`} onClick={() => setDecision({ c, statut: 'REFUSE', commentaire: '' })}><CloseOutlined fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Modifier"><IconButton size="small" aria-label={`Modifier ${c.nom}`} onClick={() => setDialog(c)}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                          </>
                        )}
                        {c.statut === 'APPROUVE' && (
                          <Tooltip title="Annuler ce congé"><IconButton size="small" aria-label={`Annuler le congé de ${c.nom}`} onClick={() => setDecision({ c, statut: 'ANNULE', commentaire: '' })}><BlockOutlined fontSize="small" /></IconButton></Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {list.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}>{tab === 0 ? 'Aucune demande en attente' : 'Aucun congé cette année'}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {tab === 2 && (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employé</TableCell><TableCell align="right">Droit {annee}</TableCell><TableCell align="right">Report</TableCell>
                  <TableCell align="right">Pris</TableCell><TableCell align="right">En attente</TableCell><TableCell align="right">Solde</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {soldes.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell><b>{s.nom} {s.prenom}</b> <span style={{ fontSize: 12, color: '#5B6675' }}>{s.matricule}</span></TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{s.droit}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{s.report}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{s.pris}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{s.en_attente || ''}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 700, color: s.solde < 0 ? 'error.main' : undefined }}>{s.solde}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 1.5, fontSize: 12.5, color: 'text.secondary' }}>
            Congés annuels uniquement, comptés en jours ouvrés (jours de repos et fériés exclus). Le droit annuel se règle dans Paramètres RH, le report dans chaque fiche employé.
          </Box>
        </Card>
      )}

      <CongeDialog open={!!dialog} value={dialog} employes={employes} soldes={soldes} onClose={() => setDialog(null)}
        onSaved={(m) => { setDialog(null); setToast(m); load(); }} />

      <Dialog open={!!decision} onClose={() => setDecision(null)} fullWidth maxWidth="xs">
        {decision && (
          <>
            <DialogTitle>
              {{ APPROUVE: 'Approuver le congé', REFUSE: 'Refuser la demande', ANNULE: 'Annuler le congé' }[decision.statut]}
            </DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
              {decision.error && <Alert severity="error">{decision.error}</Alert>}
              <Box>
                {decision.c.prenom} {decision.c.nom} — {TYPES_CONGE[decision.c.type]}, du {dateFr(decision.c.date_debut)} au {dateFr(decision.c.date_fin)} ({decision.c.nb_jours} jours ouvrés)
              </Box>
              <TextField label={decision.statut === 'REFUSE' ? 'Motif du refus' : 'Commentaire (facultatif)'} required={decision.statut === 'REFUSE'}
                value={decision.commentaire} multiline minRows={2} onChange={(e) => setDecision({ ...decision, commentaire: e.target.value })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDecision(null)}>Retour</Button>
              <Button variant="contained" color={decision.statut === 'APPROUVE' ? 'success' : 'error'} onClick={decide}>
                {{ APPROUVE: 'Approuver', REFUSE: 'Refuser', ANNULE: 'Annuler le congé' }[decision.statut]}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default RhConges;
